<p-confirmDialog [closable]="false" [key]="confirmDialogKey" [baseZIndex]="1000"></p-confirmDialog>

<div *ngIf="!isPageReady" class="flex items-center justify-center h-full">
  <p-progressSpinner strokeWidth="3" animationDuration="1s"></p-progressSpinner>
</div>

<div *ngIf="isPageReady" class="flex h-full flex-col">
  <!-- Summary Bar -->
  <div class="flex shrink-0 items-center gap-6 border-b px-6 py-3" style="background: var(--p-surface-ground, #fafafa)">
    <div class="font-semibold">Bin {{ shipBin.id }}</div>
    <span class="text-sm"
      >Remaining: <strong>{{ totalRemaining }}</strong></span
    >
    <span class="text-sm"
      >In Bin: <strong>{{ totalInBin }}</strong></span
    >
    <span class="text-sm"
      >Open After Release: <strong>{{ totalOpenAfterRelease }}</strong>
      <span *ngIf="totalOpenAfterRelease > 0" style="color: var(--p-text-muted-color, #6b7280)">
        (<span *ngIf="totalShipPrep > 0">{{ totalShipPrep }} ship prep</span
        ><span *ngIf="totalShipPrep > 0 && (totalInProcess > 0 || totalBackorder > 0)">, </span
        ><span *ngIf="totalInProcess > 0">{{ totalInProcess }} in-process</span
        ><span *ngIf="totalInProcess > 0 && totalBackorder > 0">, </span
        ><span *ngIf="totalBackorder > 0">{{ totalBackorder }} backordered</span>)
      </span>
    </span>
    <div class="mx-2" style="border-left: 2px solid var(--p-primary-200, #bfdbfe); height: 1.5rem"></div>
    <div class="flex items-center gap-1">
      <i class="pi pi-check-circle" style="color: var(--p-green-500, #22c55e); font-size: 1rem"></i>
      <span>{{ goodCount }} Ready</span>
    </div>
    <div class="flex items-center gap-1" *ngIf="warnCount > 0">
      <i class="pi pi-question-circle" style="color: var(--p-orange-500, #f97316); font-size: 1rem"></i>
      <span>{{ warnCount }} Warning</span>
    </div>
    <div class="flex items-center gap-1" *ngIf="blockCount > 0">
      <i class="pi pi-times-circle" style="color: var(--p-red-500, #ef4444); font-size: 1rem"></i>
      <span>{{ blockCount }} Blocked</span>
    </div>
  </div>

  <!-- JO Cards -->
  <div class="min-h-0 flex-1 overflow-y-auto p-4">
    <div class="flex flex-col gap-3">
      <div *ngFor="let jo of joStatuses" class="qb-detail-card p-4" [style.border-left]="'4px solid ' + getStatusColor(jo.status)">
        <!-- Card Header -->
        <div class="flex items-center gap-3">
          <i [class]="getStatusIcon(jo.status)" [style.color]="getStatusColor(jo.status)" style="font-size: 1.5rem"></i>
          <div class="flex-1">
            <div class="flex flex-wrap items-center gap-3">
              <span class="text-lg font-semibold">JO {{ jo.joHeaderId }}</span>
              <span style="color: var(--p-text-muted-color, #6b7280)">{{ jo.accountName }}</span>
              <span style="color: var(--p-text-muted-color, #6b7280)">{{ jo.employeeName }}</span>
            </div>
            <div class="flex flex-wrap items-center gap-4 text-sm">
              <span class="font-semibold" [style.color]="getStatusColor(jo.status)">
                {{ getStatusLabel(jo) }}
              </span>
              <span *ngIf="jo.nonShippableCount > 0" class="font-semibold" style="color: var(--p-red-500, #ef4444)">
                Damaged or missing items in this bin: {{ jo.nonShippableCount }}. Remove them before you release the bin.
              </span>
              <span style="color: var(--p-text-muted-color, #6b7280)"> Exp. Ship: {{ jo.expectedShipDate | date: 'MM/dd/yyyy' }} </span>
              <span
                >Remaining: <strong>{{ jo.remainingToShip }}</strong></span
              >
              <span
                >In Bin: <strong>{{ jo.binItemCount }}</strong></span
              >
              <span
                >Open After Release: <strong>{{ jo.openAfterRelease }}</strong>
                <span *ngIf="jo.openAfterRelease > 0" style="color: var(--p-text-muted-color, #6b7280)">
                  (<span *ngIf="jo.shipPrepCount > 0">{{ jo.shipPrepCount }} ship prep</span
                  ><span *ngIf="jo.shipPrepCount > 0 && (jo.inProcessCount > 0 || jo.backorderQty > 0)">, </span
                  ><span *ngIf="jo.inProcessCount > 0">{{ jo.inProcessCount }} in-process</span
                  ><span *ngIf="jo.inProcessCount > 0 && jo.backorderQty > 0">, </span
                  ><span *ngIf="jo.backorderQty > 0">{{ jo.backorderQty }} backordered</span>)
                </span>
              </span>
              <span *ngIf="jo.nextExpectedReceiveDate" style="color: var(--p-text-muted-color, #6b7280)">
                Next Receive: {{ jo.nextExpectedReceiveDate | date: 'MM/dd/yyyy' }}
              </span>
            </div>
          </div>
          <!-- Collapse toggle -->
          <button
            *ngIf="jo.status !== 'good'"
            pButton
            type="button"
            class="p-button-text p-button-sm p-button-rounded"
            [icon]="jo.isCollapsed ? 'pi pi-chevron-down' : 'pi pi-chevron-up'"
            [title]="jo.isCollapsed ? 'Expand details' : 'Collapse details'"
            (click)="jo.isCollapsed = !jo.isCollapsed"
          ></button>
        </div>

        <!-- Collapsible Detail Lists -->
        <div *ngIf="jo.status !== 'good' && !jo.isCollapsed" class="ml-11 mt-2 flex flex-col gap-2">
          <!-- In-Process Items -->
          <div *ngIf="jo.inProcessItems.length > 0">
            <div class="mb-1 text-sm font-semibold" style="color: var(--p-red-500, #ef4444)">
              In-Process Items ({{ jo.inProcessItems.length }})
            </div>
            <table class="qb-info-table w-full text-sm">
              <tr *ngFor="let itd of jo.inProcessItems">
                <td class="label" style="width: 130px">{{ itd.id }}</td>
                <td class="value">{{ itd.product?.productType?.description }}</td>
                <td class="value" style="width: 200px">{{ itd.product?.productType?.manufacturer?.name }}</td>
                <td class="value" style="width: 260px">{{ itd.itemDetailStatus?.name }}</td>
              </tr>
            </table>
          </div>

          <!-- Backorder Items -->
          <div *ngIf="jo.backorderItems.length > 0">
            <div class="mb-1 text-sm font-semibold" style="color: var(--p-orange-500, #f97316)">
              Backorders ({{ jo.backorderItems.length }})
            </div>
            <table class="qb-info-table w-full text-sm">
              <tr *ngFor="let pod of jo.backorderItems">
                <td class="label" style="width: 130px">PO {{ pod.poHeaderId }}</td>
                <td class="value">{{ pod.product?.productType?.description }}</td>
                <td class="value" style="width: 200px">{{ pod.poHeader?.manufacturer?.name }}</td>
                <td class="value" style="width: 80px; text-align: right">Qty: {{ pod.getRemainingQty() }}</td>
                <td class="value" style="width: 120px">{{ pod.poHeader?.poStatus?.name }}</td>
                <td class="value" style="width: 140px">Exp: {{ pod.expectedReceiveDate | date: 'MM/dd/yyyy' }}</td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div *ngIf="joStatuses.length === 0" class="py-8 text-center" style="color: var(--p-text-muted-color, #6b7280)">
        No job orders in this bin.
      </div>
    </div>
  </div>

  <!-- Footer -->
  <div class="flex shrink-0 gap-2 border-t p-3">
    <button
      pButton
      type="button"
      class="p-button-raised"
      icon="pi pi-send"
      [disabled]="!canRelease || isReleasing"
      [pTooltip]="releaseBlockedReason"
      (click)="releaseForShipping()"
    >
      <span>Release &amp; Create Vouchers</span>
    </button>
    <button pButton type="button" class="p-button-raised" (click)="onClose()">
      <span>Close</span>
    </button>
  </div>
</div>
